import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { completeDigiLockerKyc } from '../../api/useKyc';
import { fetchAndStoreUserDetails } from '../../services/AuthService';
import { useTokenStore } from '../../storage/tokenState';
import type { ELanguage } from '../../storage/store';
import { AADHAAR_DOC_TYPE } from '../../interfaces/Kyc';
import {
  clearPendingKycSession,
  getPendingKycSession,
  recordKycAttempt,
  resetKycAttempts,
  setPendingKycSession,
  type IKycPendingSession,
} from './helper';

/**
 * Automatic attempts before completion stops trying on its own.
 *
 * A session the backend will never resolve otherwise costs one request on every
 * boot, forever. The session itself is kept — the user can still drive it from
 * the banner or the /kyc screen, and a manual retry restores the budget.
 */
const MAX_AUTO_ATTEMPTS = 3;

interface IUseCompletePendingKyc {
  /** Called once the store actually reflects an approved KYC. */
  onApproved?: (session: IKycPendingSession) => void;
}

/**
 * Finishes a verification the user has returned from.
 *
 * Deliberately not tied to the `/kyc` route. The redirect target is configured
 * on the backend and is not guaranteed to land here — an unmatched path falls
 * through the catch-all in `Routes.tsx` to `/wallet` — so this is mounted on
 * both screens and completes from whichever one the browser reaches.
 *
 * The `clientId` normally comes from the parked session, and from the return
 * query string when that has been lost with the tab.
 */
export function useCompletePendingKyc({
  onApproved,
}: IUseCompletePendingKyc = {}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { token: userToken, language } = useTokenStore();

  const [isCompleting, setIsCompleting] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const inFlightRef = useRef(false);

  // Vendor param naming is unconfirmed, so both spellings are accepted.
  const clientIdFromUrl =
    searchParams.get('client_id') || searchParams.get('clientId');

  const resolveSession = useCallback((): IKycPendingSession | null => {
    const stored = getPendingKycSession();
    if (stored) return stored;

    // Storage lost with the tab. The query param is enough to finish, and
    // `docType` is Aadhaar for every flow this screen starts.
    if (clientIdFromUrl) {
      return { clientId: clientIdFromUrl, docType: AADHAAR_DOC_TYPE };
    }
    return null;
  }, [clientIdFromUrl]);

  const complete = useCallback(async (isManual = false) => {
    const session = resolveSession();
    if (!session || inFlightRef.current) return;

    // Park a session that only exists in the URL before doing anything else.
    // A logged-out return bails below, and the route guard's redirect to sign-in
    // drops the query string — so without this the clientId is gone for good and
    // nothing server-side can finish the verification.
    if (!getPendingKycSession()) {
      setPendingKycSession(session);
    }

    // Completing requires an authenticated call; Wallet is a public route.
    if (!userToken) return;

    // Spent budget: keep the session and the manual affordances, stop spending a
    // request per boot on it. Checked after the auth gate, so a logged-out boot
    // — which never issues a request — costs nothing from the budget.
    if (!isManual && (session.attempts ?? 0) >= MAX_AUTO_ATTEMPTS) {
      setHasPending(true);
      return;
    }
    if (!isManual) recordKycAttempt();

    inFlightRef.current = true;
    setIsCompleting(true);
    try {
      const result = await completeDigiLockerKyc(
        session.clientId,
        session.docType,
      );

      // The gate reads kycStatus from the store, so it has to be refreshed
      // before the user is sent anywhere. This call swallows its own errors and
      // reports a boolean rather than throwing.
      const refreshed = await fetchAndStoreUserDetails(language as ELanguage);

      if (result.status === 'APPROVED') {
        if (!refreshed) {
          // Approved upstream but the store is stale. Navigating now would hit
          // the KYC gate again and bounce the user straight back here, so keep
          // the session and let them retry the refresh instead.
          inFlightRef.current = false;
          setHasPending(true);
          toast.error(t('errorOccurred'));
          return;
        }

        clearPendingKycSession();
        setHasPending(false);
        toast.success(result.message || t('kycApprovedToast'));
        onApproved?.(session);
        return;
      }

      // Rejected, but not necessarily spent. SurePass is initialised with
      // `retry_count: 2`, so a provider failure leaves the session usable and
      // the backend says so with `retryAllowed`. Discarding it there would make
      // the next attempt pay for a fresh `initialize`, which is billed.
      if (result.retryAllowed) {
        inFlightRef.current = false;
        setHasPending(true);
        toast.error(result.message || t('kycRejectedToast'));
        return;
      }

      // Genuinely spent (underage, retries exhausted): drop it regardless of
      // whether the refresh landed.
      clearPendingKycSession();
      setHasPending(false);
      toast.error(result.message || t('kycRejectedToast'));
    } catch (error) {
      // Leave the session in place: the attempt may still be resolvable, and the
      // user can retry rather than starting verification over.
      inFlightRef.current = false;
      setHasPending(true);
      toast.error((error as Error)?.message || t('errorOccurred'));
    } finally {
      setIsCompleting(false);
    }
  }, [resolveSession, userToken, language, t, onApproved]);

  useEffect(() => {
    setHasPending(Boolean(resolveSession()));
    complete();
    // `complete` is stable for a given session; running once per mount is the
    // intent, with the manual retry below covering everything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => {
    inFlightRef.current = false;
    // A deliberate retry restores the automatic budget too: the user coming back
    // is the best evidence there is that the session is still worth completing.
    resetKycAttempts();
    complete(true);
  }, [complete]);

  return { isCompleting, hasPending, retry };
}
